<template>
    <app-layout title="Survey defaults">
        <template v-slot:header>
            <div class="flex justify-between">
                <h2 class="font-bold text-4xl dark:text-gray-200">
                    My organisation settings
                </h2>
            </div>
        </template>
        <nav class="flex flex-col sm:flex-row border-b rounded-t-md border-dark mb-6">
            <button v-for="surveyType in surveyTypes"
                    class="w-1/2 py-2 px-6 text-xl block font-bold rounded-t-md transition-colors duration-150"
                    :class="{
                    'bg-dark text-white' : currentSurveyType === surveyType.id,
                    'text-gray-600 hover:text-gray-800 dark:hover:text-gray-200' : currentSurveyType !== surveyType.id
                }"
                    @click="currentSurveyType = surveyType.id"
            >
                {{ surveyType.type }}
            </button>
        </nav>

        <div v-for="surveyType in surveyTypes" v-show="currentSurveyType === surveyType.id">
            <profile-fields-form :survey-type="surveyType.id"
                                 :profile-fields="profileFields[surveyType.id]"
                                 :system-profile-fields="systemProfileFields[surveyType.id] || []"
            ></profile-fields-form>

            <survey-defaults-form :survey-type="surveyType.id"
                                  :defaults="defaults[surveyType.id]"
            ></survey-defaults-form>

            <survey-properties-form v-if="can('manage unit prices')" :survey-type="surveyType.id"
                                    :properties="properties[surveyType.id]"
            ></survey-properties-form>

            <survey-prices-form v-if="can('manage survey templates')" :survey-type="surveyType.id"
                                :prices="prices[surveyType.id]"
            ></survey-prices-form>

            <template v-if="can('manage survey templates')">
                <survey-default-labels-form v-for="questionType in questionTypes" :survey-type="surveyType.id"
                                            :answerLabelOptions="answerLabelOptions[surveyType.id][questionType.id]"
                                            :questionType="questionType"
                ></survey-default-labels-form>
            </template>

        </div>

    </app-layout>
</template>

<script>
import AppLayout from "@/Layouts/AppLayout";
import JetInput from "@/Jetstream/Input";
import JetCheckbox from "@/Jetstream/Checkbox";
import JetLabel from "@/Jetstream/Label";
import {ref, computed} from 'vue';
import ProfileFieldsForm from "@/Pages/Admin/Survey/Settings/Partials/ProfileFieldsForm";
import SurveyDefaultsForm from "@/Pages/Admin/Survey/Settings/Partials/SurveyDefaultsForm";
import SurveyPropertiesForm from "@/Pages/Admin/Survey/Settings/Partials/SurveyPropertiesForm";
import SurveyPricesForm from "@/Pages/Admin/Survey/Settings/Partials/SurveyPricesForm";
import {usePage} from '@inertiajs/inertia-vue3';
import SurveyDefaultLabelsForm from "@/Pages/Admin/Survey/Settings/Partials/SurveyDefaultLabelsForm";

export default {
    components: {
        SurveyPropertiesForm,
        SurveyDefaultsForm,
        ProfileFieldsForm,
        SurveyPricesForm,
        SurveyDefaultLabelsForm,
        AppLayout,
        JetInput,
        JetCheckbox,
        JetLabel
    },
    props: {
        surveyTypes: {
            type: Array,
            required: true,
        },
        prices: {
            type: Object,
            required: true,
        },
        systemProfileFields: {
            type: Object,
            required: true,
        },
        questionTypes: {
            type: Array,
            required: true,
        }

    },
    setup(props) {
        const pageProps = () => usePage().props.value;
        const currentSurveyType = ref(1);

        const {systemProfileFields} = pageProps();

        const defaults = computed(() => {
            const defaults = {};
            const {surveyDefaults} = pageProps();
            props.surveyTypes.forEach((surveyType) => {
                if (surveyDefaults[surveyType.id]) {
                    const {logo, logo_url, intro_text, email_text} = surveyDefaults[surveyType.id];
                    defaults[surveyType.id] = {
                        logo,
                        logo_url,
                        intro_text,
                        email_text
                    };
                } else {
                    defaults[surveyType.id] = {
                        logo: null,
                        logo_url: null,
                        intro_text: null,
                        email_text: null,
                    };
                }
            });
            return defaults;
        });

        const profileFields = computed(() => {
            const profileFields = {};
            const {surveyDefaults} = pageProps();
            props.surveyTypes.forEach((surveyType) => {
                if (surveyDefaults[surveyType.id] && surveyDefaults[surveyType.id].profile_fields) {
                    profileFields[surveyType.id] = surveyDefaults[surveyType.id].profile_fields;
                } else {
                    profileFields[surveyType.id] = [];
                }
            });
            return profileFields;
        });

        const checkAnswerLabelOptionsStructure = (options) => {
            props.questionTypes.forEach((questionType) => {
                if (!options[questionType.id]) {
                    switch (questionType.id) {
                        case 1:
                            options[questionType.id] = {
                                1: {
                                    bgColor: null,
                                    textColor: null,
                                    label: '',
                                },
                                2: {
                                    bgColor: null,
                                    textColor: null,
                                    label: '',
                                },
                                3: {
                                    bgColor: null,
                                    textColor: null,
                                    label: '',
                                },
                                4: {
                                    bgColor: null,
                                    textColor: null,
                                    label: '',
                                },
                                5: {
                                    bgColor: null,
                                    textColor: null,
                                    label: '',
                                },
                            };
                            break;
                        default:
                            options[questionType.id] = {};
                            break;
                    }
                }
            });
            return options;
        }

        const answerLabelOptions = computed(() => {
            const answerLabelOptions = {};
            const {surveyDefaults} = pageProps();
            props.surveyTypes.forEach((surveyType) => {
                if (surveyDefaults[surveyType.id] && surveyDefaults[surveyType.id].answer_label_options) {
                    answerLabelOptions[surveyType.id] = surveyDefaults[surveyType.id].answer_label_options;
                } else {
                    answerLabelOptions[surveyType.id] = {};
                }

                answerLabelOptions[surveyType.id] = checkAnswerLabelOptionsStructure(answerLabelOptions[surveyType.id]);
            });
            return answerLabelOptions;
        });

        let properties = computed(() => {
            const properties = {};
            const {surveyDefaults} = pageProps();
            props.surveyTypes.forEach((surveyType) => {
                if (surveyDefaults[surveyType.id] && surveyDefaults[surveyType.id].properties) {
                    properties[surveyType.id] = surveyDefaults[surveyType.id].properties;
                } else {
                    properties[surveyType.id] = {
                        anytime_report: false,
                        reopen: false,
                        live_analytics: false,
                    };
                }
            });
            return properties;
        });

        return {
            currentSurveyType,
            defaults,
            profileFields,
            properties,
            systemProfileFields,
            answerLabelOptions,
        }
    }
}
</script>

<style scoped>

</style>
